import { API, Auth, Storage } from 'aws-amplify'
import { AppEntityConfig } from "../redux/AppEntities";
import { AccountingUtils } from "revlock-accounting";
import UserActions from "redux/actions/user.actions";
import queryString from 'query-string';
import { showAlert, showError, getMicroStrategyGroupName } from "../utils/WebUtils";
import { Utils } from 'revlock-webutils';
const customAuthorizationEnabled =
    process.env.REACT_APP_CUSTOM_AUTHORIZATION && process.env.REACT_APP_CUSTOM_AUTHORIZATION === "true";

Storage.configure({ level: "public" });

const orgId = (state) => {
    let org = state && state.currentOrganization && state.currentOrganization.currentOrganization;
    if (!org) {
        throw Error("No organization selector, error while initiating update.");
    }
    return org.id;
};

export const PRIVATE_API_NAME = 'revlock_api';
export const PUBLIC_API_NAME = 'revlock_public_api'

export const fetcher = (entityName) => async function (state, args) {
    let clientId = orgId(state);
    let config = AppEntityConfig(entityName);
    let getAllPath = config.getAllPath;

    if (config.paging) {
        let { currentPage, maxPerPage } = args;

        // Start index of items to show on screen
        args.start = currentPage * maxPerPage;

        // End index of items to show on screen.
        args.end = args.start + maxPerPage - 1;
    }

    Object.keys(args).forEach(arg => {
        if (args[arg] instanceof Date) {
            args[arg] = args[arg].toISOString();
        }
    });

    return invokeApi(GET, `/private/${clientId}/${getAllPath}`, {
        queryStringParameters: args
    });
};

export const fetcherCurrent = (entityName) => async function (state, entityId) {
    const clientId = orgId(state);
    const path = AppEntityConfig(entityName).getPath;

    return invokeApi(GET, `/private/${clientId}/${path}/${entityId}`);
};

export const fetcherCurrentTask = (entityName) => async function (state, args) {
    let clientId = orgId(state);
    let { entityId } = args;

    return invokeApi(GET, `/private/${clientId}/activeTasks/all/${entityId}`, {
        queryStringParameters: args
    });
};

export const assignCurrentTask = (entityName) => async function (state, params) {
    let clientId = orgId(state);

    let { taskId } = params;

    return invokeApi(POST, `/private/${clientId}/task/${taskId}/claim`, {
        body: params
    });
};

export const onEntityAction = (entityName) => async function (state, action, params) {
    let clientId = orgId(state);

    let { url, method } = action

    return invokeApi(method || POST, `/private/${clientId}/${url}`, method !== GET ? {
        body: params
    } : {
        queryStringParameters: params
    });
};

export const creator = (entityName) => async function (state, updates) {
    let clientId = orgId(state);
    let updatePath = AppEntityConfig(entityName).updatePath;

    return invokeApi(POST, `/private/${clientId}/${updatePath}`, {
        body: updates
    });
};

export const updater = (entityName) => async function (state, updates) {
    let clientId = orgId(state);
    let updatePath = AppEntityConfig(entityName).updatePath;

    return invokeApi(PUT, `/private/${clientId}/${updatePath}`, {
        body: updates
    });
};

export const deleteAll = (entityName) => async function (state, ids) {
    let clientId = orgId(state);
    let updatePath = AppEntityConfig(entityName).updatePath;

    return invokeApi(DELETE, `/private/${clientId}/${updatePath}`, {
        body: ids
    });
};

export async function initDemoOrg(state, org) {
    return invokeApi(GET, `/private/${org.id}/_demoReLoad`);
}


export async function initProdTest(state, org) {
    return invokeApi(GET, `/private/${org.id}/_demoReferenceLoad`);
}

export async function getOrgConfigurations(clientId) {
    return invokeApi(GET, `/private/${clientId}/orgConfigs`);
}

export async function putOrgConfigurations(clientId, configs) {
    return invokeApi(PUT, `/private/${clientId}/orgConfigs`, {
        body: configs
    });
}

export async function deleteOrgConfigurations(clientId, orgConfigIds) {
    return invokeApi(DELETE, `/private/${clientId}/orgConfigs`, {
        body: orgConfigIds
    });
}

/**
 * Clear RA Cache
 */
export async function clearRACache(orgId) {
    return invokeApi(POST, `/private/admin/${orgId}/clear-arrangements`);
}
/**
 * Impersonation
 */
export async function startImpersonation(impersonatedUser) {
    return invokeApi(PUT, `/private/admin/impersonate`, { body: { impersonatedUser } });
}

export async function endImpersonation() {
    return invokeApi(DELETE, `/private/admin/impersonate`);
}
/**
 * DB Size
 */

export async function getDBSize() {
    return invokeApi(GET, `/private/admin/redis/dbSize`);
}

/**
 * Demo environment details
 *
 * @returns {Promise<*|void|undefined>}
 */
export async function getDemoEnvironmentDetails() {
    const url = '/demoenvdetails'
    return invokeApi(GET, url, {}, PUBLIC_API_NAME);
}

/**
 * What mode is the system running..? This is either {mode: "r"} or {mode: "rw"}
 *
 * @returns {Promise<*|void|undefined>}
 */
export async function getMode(details) {
    const url = details ? `/mode?details=${details}` : `/mode`;
    return invokeApi(GET, url, {}, PUBLIC_API_NAME, true);
}

/**
 * Set the REVLOCK system mode that is used for upgrades.
 *
 * @param mode either "r" or "rw"
 * @returns {Promise<*|void|undefined>}
 */
export async function setMode(mode = 'rw') {
    return invokeApi(PUT, `/private/admin/mode/${mode}`);
}

export async function saveInDirectExpenseItems(expenseItems, orgId) {
    const salesOrderId = expenseItems[0].salesOrderId
    return invokeApi(PUT, `/private/${orgId}/indirect-expenses/${salesOrderId}`, {
        body: expenseItems
    });
}

export async function saveExpenseItems(expenseItems, orgId) {
    const salesOrderId = expenseItems[0].salesOrderId
    return invokeApi(PUT, `/private/${orgId}/direct-expenses/${salesOrderId}`, {
        body: expenseItems
    });
}
/**
* Auto Suggest API
*/
export async function suggestOptions(params, orgId) {
    const toReturn = await invokeApi(GET, `/private/${orgId}/partialKeySearch?${queryString.stringify(params)}`);
    return toReturn
}

/**
* Customers
*/
export async function getCustomer(customerId, orgId, reqFromSalesOrderPage = false) {
    return invokeApi(GET, `/private/${orgId}/customer/${customerId}`, {
        queryStringParameters: { reqFromSalesOrderPage }
    });
}

export async function getCustomers(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/customers`, {
        queryStringParameters: searchParams
    });
}

/**
 * Errors
 */

export async function getErrors(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errors`, {
        queryStringParameters: searchParams,
    })
}

/**
 * ErrorCode
 */

export async function getErrorCode(errorCode) {
    return invokeApi(GET, `/private/errorCode`, {
        queryStringParameters: {
            errorCode,
        },
    })
}

/**
 * ErrorInputSource
 */

export async function getErrorInputSource(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errorInputSource`, {
        queryStringParameters: searchParams,
    })
}

/**
 * ErrorInputSourceColumns
 */

export async function getErrorInputSourceColumns(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errorInputSourceColumns`, {
        queryStringParameters: searchParams,
    })
}

/**
 * Products
 */
export async function getChildProducts(org, productId) {
    return invokeApi(GET, `/private/${org.id}/products/${Utils.encodeId(productId)}`);
}

export async function updateProducts(org, products) {
    return invokeApi(PUT, `/private/${org.id}/product`, {
        body: products
    });
}

/** Helper for performing sales order and export related filter calls. */
async function performSalesOrdersFilteredAction(params, url, method = GET) {
    const salesOrderSearchParams = Object.assign({}, params);

    return invokeApi(method, url, {
        queryStringParameters: salesOrderSearchParams
    });
}

/**
 * Export
 */
export async function getExportOptions(params) {
    return invokeApi(GET, `/private/${params.orgId}/export-options`);
}

export async function getExportCount(params) {
    return await performSalesOrdersFilteredAction(params, `/private/${params.orgId}/export/${params.exportType}/count`);
}

export async function exportData(params) {
    return await performSalesOrdersFilteredAction(params, `/private/${params.orgId}/export/${params.exportType}`, POST);
}

export async function getExportedFiles(states, params) {
    return invokeApi(GET, `/private/${params.orgId}/exports`, {
        queryStringParameters: params
    });
}

export async function exportClientData(params) {
    return invokeApi(POST, `/private/${params.orgId}/exports`, {
        queryStringParameters: params
    });
}

/**
 * Revenue Story
 */
export async function getAvailableRevenueStoryTypes(orgId) {
    return invokeApi(GET, `/private/${orgId}/revenuestorytypes`);
}

export async function getRevenueStory(orgId, params = {}) {
    return invokeApi(GET, `/private/${orgId}/revenuestory`, {
        queryStringParameters: params
    });
}

export async function exportRevenueStory(orgId, params = {}) {
    return invokeApi(POST, `/private/${orgId}/revenuestory/export`, {
        queryStringParameters: params
    });
}

/**
 * SalesOrders
 */
export async function getSalesOrders(orgId, params) {
    return await performSalesOrdersFilteredAction(
        params, `/private/${orgId}/salesOrders`);
}

export async function updateSalesOrder(state, clientId) {
    return invokeApi(PUT, `/private/${clientId}/salesOrder`);
}

export async function deleteSalesOrder(clientId, salesOrderId) {
    return invokeApi(DELETE, `/private/${clientId}/salesOrder/${salesOrderId}`);
}

export async function getMerchant(domain) {
    return invokeApi(GET, `/merchants`, {
        queryStringParameters: {
            website: Utils.encodeId(domain, true)
        }
    });
}
export async function updateMerchant(params) {
    return invokeApi(PATCH, `/merchants`, {
        body: params
    });
}

export async function adminGetMerchant(domain) {
    return invokeApi(GET, `/private/admin/merchant?domain=${domain}`);
}

export async function adminAddMerchant(params) {
    return invokeApi(POST, `/private/admin/merchant`, {
        body: params
    });
}

export async function adminUpdateMerchant(params) {
    return invokeApi(PUT, `/private/admin/merchant`, {
        body: params
    });
}

/**
 * Organizations
 */
export async function signUpClient({ name, domain }) {
    name = name || domain
    //default to domain name for client if not available

    return invokeApi(POST, `/org`, {
        body: {
            name,
            domain
        }
    });
}

export async function companyExists({ website, tenant, query }) {
    return invokeApi(GET, `/tenants`, {
        queryStringParameters: { website, tenant, query }
    })
}

export async function signUpTenant(params) {
    return invokeApi(POST, `/tenants`, {
        body: params
    });
}

export async function updateOrganizationAttribute(orgId, params) {
    return invokeApi(PATCH, `/private/${orgId}/org`, {
        body: params
    });
}

export async function updateOrganization(state, org, periodType) {
    return invokeApi(PUT, `/private/${org.id}/org`, {
        body: {
            org,
            periodType
        }
    });
}

export async function resyncWarehouse(clientId) {
    const path = `/private/${clientId}/org/warehouse/resync`;
    return invokeApi(POST, path, {});
}

export async function contactSales(clientId, type, name) {
    const path = `/private/${clientId}/org/license/inquiry`;

    return invokeApi(POST, path, {
        body: {
            type, name
        }
    });
}

export async function upgradeLicensing(clientId, type, name, trial) {
    const path = `/private/${clientId}/org/license/upgrade`;

    return invokeApi(POST, path, {
        body: {
            type, name, trial
        }
    });
}

export async function salesOrderExists(clientId, salesOrderId) {
    return invokeApi(GET, `/private/${clientId}/salesOrder/${Utils.encodeId(salesOrderId)}/exists`)
}

export async function salesOrdersExists(clientId) {
    return invokeApi(GET, `/private/${clientId}/salesOrder/exists`)
}

export async function isReSyncRequired(clientId) {
    return salesOrdersExists(clientId);
}

export async function inviteUserForOrganization(params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;
    return invokeApi(POST, path, {
        body: params
    });
}

export async function removeUserFromOrganization(state, params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;
    return invokeApi(DELETE, path);
}

export async function updateOrganzationUserRoles(state, params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;

    return invokeApi(PUT, path, {
        body: { roles: params.roles }
    });
}

export async function getOrganizationUsers(state, orgId) {
    return invokeApi(GET, `/private/${orgId}/users`);
}

export async function getWHBackups() {
    return invokeApi(GET, `/private/admin/whBackups`);
}

export async function restoreWH(database, s3Url, fileId) {
    return invokeApi(POST, `/private/admin/whRestore`, {
        body: {
            database,
            s3Url,
            fileId
        }
    });
}

export async function resetMarkedEnvironmentsForCleanup() {
    return invokeApi(POST, '/private/admin/orgs/expired');
}
export async function getMarkedEnvironmentsForCleanup(searchParams) {
    return invokeApi(GET, '/private/admin/orgs/expired', {
        queryStringParameters: searchParams
    });
}

export async function cleanUpEnvironments(params) {
    return invokeApi(DELETE, '/private/admin/orgs', {
        body: params
    });
}

export async function getAllOrganizations(start = 0, limit = 10, search = "", lookupStats = false) {
    return invokeApi(GET, `/private/admin/orgs?start=${start}&limit=${limit}&search=${search}&lookupStats=${lookupStats}`);
}

export async function upgradeOrganization(tenantIds, versionTo, allTenants, numTenantsToUpgrade) {
    return invokeApi(POST, `/private/admin/upgrade`, {
        body: {
            tenantIds,
            versionTo,
            allTenants,
            numTenantsToUpgrade
        }
    });
}

export async function deleteOrganization(orgId) {
    return invokeApi(DELETE, `/private/admin/${orgId}/org`);
}

export async function initiateRecon() {
    return invokeApi(POST, `/private/admin/orgs/recon`);
}

export async function invokeRegression({ scenario, branch, email }) {
    return invokeApi(POST, `/private/admin/run-regression`, { body: { scenario, email, branch } });
}

export async function recordJobs({ clientId, scenario, firstPeriod, branch }) {
    return invokeApi(POST, `/private/admin/recordJob`, { body: { clientId, scenario, firstPeriod, branch } });
}

export async function generateJournalEntries(clientId, startPeriod, endPeriod) {
    const url = `/private/${clientId}/journal-entries`

    return invokeApi(POST, url, {
        body: {
            startPeriod,
            endPeriod
        }
    });
}
export async function setAccountingPeriod(clientId, period) {
    const url = `/private/${clientId}/org/period/accounting`
    return invokeApi(PUT, url, {
        body: {
            period
        }
    });
}

export async function setAccountingConversionDate(clientId, conversionDate) {
    const url = `/private/${clientId}/org/conversion/accounting`
    return invokeApi(PUT, url, {
        body: {
            conversionDate
        }
    });
}

export async function setReportingPeriod(clientId, startPeriodsAgo) {
    const url = `/private/${clientId}/org/period/reporting`
    return invokeApi(PUT, url, {
        body: {
            startPeriodsAgo
        }
    });
}

export async function updateHomeCurrency(clientId, currency) {
    const url = `/private/${clientId}/organization/currency`
    return await invokeApi(PUT, url, {
        body: {
            currency
        }
    });
}

export async function clearSSPRules(clientId) {
    return invokeApi(DELETE, `/private/${clientId}/standaloneSellingPrices`)
}

export async function getPriceAnalysis(clientId) {
    return invokeApi(GET,`/private/${clientId}/priceAnalysis`)  
}

export async function getSSPAnalysis(clientId, product) {
    return invokeApi(GET,`/private/${clientId}/sspAnalysis/${product}`)
}

export async function runSSPAnalyzerJob(clientId){
    return invokeApi(GET,`/private/${clientId}/job/sspAnalyzer`)
}

export async function updateSSPFields(clientId, config) {
    return invokeApi(PUT, `/private/${clientId}/orgConfig/sspFields`, {
        body: {
            config
        }
    });
}

export async function updateJournalMappingFields(clientId, config) {
    return invokeApi(PUT, `/private/${clientId}/orgConfig/journalMappingFields`, {
        body: {
            config
        }
    });
}

export async function republishAllSalesOrders(clientId) {
    return invokeApi(POST, `/private/${clientId}/republish`, {
        body: {}
    })
}

export async function getUser() {
    let url = `/private/user`;
    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function getAuthorizations() {
    let url = `/private/authorizations`;
    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function getImpersonatedUser(username) {
    let url = `/private/admin/user/${username}`

    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function addSuperUserToAccount(clientId, body = {}) {
    const url = `/private/admin/${clientId}/user`
    return await invokeApi(POST, url, { body: body }, PRIVATE_API_NAME)
}

export async function overrideAutoSync(clientId, salesOrderId, userId) {
    const url = `/private/${clientId}/salesOrder/${Utils.encodeId(salesOrderId)}/override-autosync`
    return await invokeApi(POST, url, { body: { userId } }, PRIVATE_API_NAME)
}

export async function getOveragePS(clientId, customerId, salesOrderId) {
    const url = `/private/${clientId}/service-delivery/${customerId}/overage`
    return await invokeApi(GET, url, { queryStringParameters: { salesOrderId } }, PRIVATE_API_NAME)
}

export async function rerunRollForward({ clientId, s3filename, forceRF, action }) {
    const url = `/private/admin/${clientId}/rollforwardTools/${action}`;
    return await invokeApi(POST, url, { body: { s3filename, forceRF } }, PRIVATE_API_NAME)
}

export async function salesOrdersUtils({ clientId, s3filename, allOrders, action }) {
    const url = `/private/admin/${clientId}/salesOrder/${action}`;
    return await invokeApi(POST, url, { body: { s3filename, allOrders } }, PRIVATE_API_NAME)
}


export async function invokeArchive(clientId) {
    const url = `/private/admin/${clientId}/archive`
    return await invokeApi(POST, url, {}, PRIVATE_API_NAME)
}

export async function invokeClone(clientId, salesOrderIds = []) {
    const url = `/private/admin/${clientId}/clone`
    return await invokeApi(POST, url, { body: { salesOrderIds } }, PRIVATE_API_NAME)
}

export async function invokeCloneConfig(clientId, destClientId) {
    const url = `/private/admin/${clientId}/cloneConfig`
    return await invokeApi(POST, url, { body: { srcClientId: clientId, destClientId } }, PRIVATE_API_NAME)
}


export async function invokePublishConfig(clientId) {
    const url = `/private/admin/${clientId}/publishConfig`
    return await invokeApi(POST, url, {}, PRIVATE_API_NAME)
}

export async function invokeRecon(clientId, params) {
    const url = `/private/admin/${clientId}/reconOrg`
    return await invokeApi(POST, url, { body: params }, PRIVATE_API_NAME)
}

export async function clearRecon(clientId){
    const url = `/private/admin/${clientId}/clearReconErrors`
    return await invokeApi(DELETE, url, {} , PRIVATE_API_NAME)
}

export async function getClones(clientId) {
    const url = `/private/admin/${clientId}/clone`
    return await invokeApi(GET, url, {}, PRIVATE_API_NAME)
}

export async function getMigrationStatus(clientId) {
    const url = `/private/admin/${clientId}/migrate`
    return await invokeApi(GET, url, {}, PRIVATE_API_NAME)
}

export async function invokeMigration(clientId, database, deleteSrcData) {
    const url = `/private/admin/${clientId}/migrate`
    return await invokeApi(POST, url, {
        queryStringParameters: { database, deleteSrcData }
    }, PRIVATE_API_NAME)
}

export async function invokeMigrationCleanup(clientId) {
    return await invokeApi(POST, `/private/admin/${clientId}/migration-cleanup`);
}

export async function restoreSnapshot(clientId, snapshot, environment) {
    const url = `/private/admin/${clientId}/restore`
    return await invokeApi(POST, url, {
        body: {
            snapshot, environment
        }
    }, PRIVATE_API_NAME);
}

export async function getUserOrganizations(withUsers = false) {
    let url = `/private/orgs`;
    if (withUsers)
        url = url + "?users=true"
    return invokeApi(GET, url);
}

export async function getOrgImages(state, orgs) {
    if (orgs) {
        let promisesOrgImages = orgs.map(org => {
            return Storage.get(org.id + ".image", { level: "public" });
        });

        let imagesResult = await Promise.all(promisesOrgImages);
        imagesResult = imagesResult.filter(element => element != null);

        orgs = orgs.map((org, index) => {
            org.image = imagesResult[index];
            return org;
        });

        // console.log("Successfully fetched user organization " + JSON.stringify(results));
        return orgs;
    }
}

export async function updateOrganizationImage(state, params) {
    let result = undefined;
    const isTestOrg = AccountingUtils.isTestId(params.orgId);
    const secondaryOrgId = isTestOrg
        ? AccountingUtils.toProdId(params.orgId)
        : AccountingUtils.toTestId(params.orgId);
    const orgIds = [params.orgId, secondaryOrgId];
    let promiseImages = orgIds.map(org =>
        Storage.put(org + ".image", params.imgFile, {
            level: "public",
            contentType: "image/png"
        })
    );
    let results = await Promise.all(promiseImages);
    results = results.filter(element => element != null);
    return Promise.resolve(results);
}


export async function updateCustomFieldTranslation(clientId, clientName, mstrProjectName, mstrLanguageName,
    isBillingMapping, newCustomFieldsMapping, originalCustomFieldsMapping) {

    const mstrGroupName = getMicroStrategyGroupName(clientId, clientName);

    return invokeApi(POST, `/private/${clientId}/microstrategy/updatecustomfield`, {
        body: {
            mstrProjectName,
            mstrLanguageName,
            mstrGroupName,
            isBillingMapping,
            newCustomFieldsMapping,
            originalCustomFieldsMapping
        }
    });
}

/**
 * Sync Jobs
 */
export async function triggerSync(state, params) {
    return invokeApi(POST, `/private/${params.orgId}/triggerSync`, {
        body: { syncParams: { s3filename: params.filename } }
    });
}

export async function archiveSync(orgId, jobId) {
    return invokeApi(PATCH, `/private/${orgId}/sync/${jobId}/archive`);
}

export async function archiveSyncJobGroup(orgId, jobId) {
    return invokeApi(PATCH, `/private/${orgId}/sync/${jobId}/archive-job-group`);
}

export async function getJobJson(orgId, jobId) {
    return invokeApi(GET, `/private/${orgId}/sync/${jobId}/json`);
}

export async function getJobGroupJson(orgId, jobGroupId) {
    return invokeApi(GET, `/private/${orgId}/sync/${jobGroupId}/json-group`);
}

export async function getSyncsStatus(orgId, queryStringParams) {
    return invokeApi(GET, `/private/${orgId}/sync/history`, {
        queryStringParameters: queryStringParams
    });
}

export async function getActiveSyncJobs(orgId) {
    return invokeApi(GET, `/private/${orgId}/sync/recent`);
}

export async function getSyncStatus(state, params) {
    return invokeApi(GET, `/private/${params.orgId}/syncStatus`, {
        queryStringParameters: { jobId: params.jobId }
    });
}

export async function getTaskSummary(state, params) {
    return invokeApi(GET, `/private/${params.orgId}/tasksSummary`);
}

export async function getCompanyId({ website, query }) {
    return invokeApi(GET, `/company`, {
        queryStringParameters: { website, query }
    }, PUBLIC_API_NAME)
}

export async function deleteEnvironment(orgId) {
    return invokeApi(DELETE, `/private/${orgId}/environment`)
}

export async function addTestTransactions(orgId, dataSet) {
    return invokeApi(POST, `/private/${orgId}/environment/demo`, {
        dataSet
    });
}

export async function getHotglueSecureToken(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/secureToken`);
}

export async function hotglueAccountingLink(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/hotglue/accounting-linked`, {
        body
    });
}

export async function hotglueAccountingUnLink(orgId, body) {
    return invokeApi(DELETE, `/private/${orgId}/hotglue/accounting-unlinked`, {
        body
    });
}

export async function getForeignExchangeRates(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/foreignExchangeRates`);
}

export async function hotglueLinkFlow(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/hotglue/link-flow`, {
        body
    });
}

export async function hotglueUnLinkFlow(orgId, body) {
    return invokeApi(DELETE, `/private/${orgId}/hotglue/unlink-flow`, {
        body
    });
}

export async function getSiteAccessKey(partner, siteKey, siteAccessKey) {
    const url = `/partners/sites/exists?partner=${partner}&siteKey=${siteKey}&siteAccessKey=${siteAccessKey}`;
    return invokeApi(GET, url);
}

export async function getPricingSummary(orgId, range) {
    const baseUrl = `/private/${orgId}/pricingSummary`;
    const url = range ? `${baseUrl}?range=${range}` : baseUrl;
    return invokeApi(GET, url);
}

export async function restoreSSPVersion(clientId, versionId) {
    return invokeApi(PUT, `/private/${clientId}/standaloneSellingPrice/version/${versionId}`)
}

export async function getSSPByVersionId(clientId, versionId) {
    return invokeApi(GET, `/private/${clientId}/standaloneSellingPrice/version/${versionId}`)
}

export async function getSSPVersions(clientId) {
    return invokeApi(GET, `/private/${clientId}/standaloneSellingPrice/versions`);
}

export async function getVersions(clientId, salesOrderId) {
    return invokeApi(GET, `/private/${clientId}/salesOrder/${Utils.encodeId(salesOrderId)}/versions`);
}

export async function restoreVersion(clientId, snapshotId) {
    return invokeApi(POST, `/private/${clientId}/salesOrder/version/${snapshotId}`);
}

export async function getSyncLockStatus(clientId) {
    return invokeApi(GET, `/private/${clientId}/lock`);
}

export async function unlockSyncLock(clientId) {
    return invokeApi(DELETE, `/private/${clientId}/lock`);
}

export async function downloadJobErrors(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/joberrors`)
}

export async function uploadIgnoreAndDeleteJobErrors(clientId, s3filename) {
    return invokeApi(POST, `/private/admin/${clientId}/joberrors`, {
        body: { s3filename }
    })
}

export async function downloadMissingSalesOrders(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/missingsalesorders`)
}

export async function getRevenueStartDate(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/revenuestartdate`)
}

export async function addAuditEvent(clientId, queryStringParams) {
    return invokeApi(POST, `/private/${clientId}/audit`, {
        queryStringParameters: queryStringParams
    });
}

export async function getAuditTrailUsers(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/usernames`);
}

export async function getAuditTrailEventTypes(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/types`);
}

export async function getAuditTrailEntityTypes(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/entities`);
}

export async function getAuditTrailEvents(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/events`, {
        queryStringParameters: queryStringParams
    });
}

export async function getAuditTrailExportCount(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/export/count`, {
        queryStringParameters: queryStringParams
    });
}

export async function exportAuditTrailEvents(clientId, queryStringParams) {
    return invokeApi(POST, `/private/${clientId}/audit/export`, {
        queryStringParameters: queryStringParams
    });
}

export async function getSecurityUsers(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/security-users`);
}

export async function getSecurityEvents(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/security-events`, {
        queryStringParameters: queryStringParams
    });
}

export async function executeGovernor () {
    return invokeApi(POST, `/private/admin/executeGovernor`, {})
}

export async function getProcesses(queryStringParams) {
    return invokeApi(GET, `/private/admin/processes`, {
        queryStringParameters: queryStringParams
    });
}

export async function cancelProcesses(clientId, jobGroupId) {
    return invokeApi(DELETE, `/private/admin/${clientId}/processes/${jobGroupId}`);
}

export async function generateGlobalExport(queryStringParams) {
    return invokeApi(POST, `/private/admin/global-exports`, {
        queryStringParameters: queryStringParams
    });
}

export const GET = async (apiName, path, init) => API.get(apiName, path, init);
export const POST = async (apiName, path, init) => API.post(apiName, path, init);
export const PUT = async (apiName, path, init) => API.put(apiName, path, init);
export const PATCH = async (apiName, path, init) => API.patch(apiName, path, init);
export const DELETE = async (apiName, path, init) => API.del(apiName, path, init);
export const HEAD = async (apiName, path, init) => API.head(apiName, path, init);

function setCookie(cName, cValue, path, minutes) {
    let d = new Date();
    d.setTime(d.getTime() + (minutes * 60 * 1000));
    let expires = "expires=" + d.toUTCString();
    document.cookie = cName + "=" + cValue + "; " + expires + ";path=" + path;
}
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
}
export async function invokeApi(method, path, myInit, apiName = PRIVATE_API_NAME, processError = true) {
    let simulatedProfile;

    try {
        myInit = myInit || {};
        myInit.response = true;

        myInit.headers = myInit.headers || {}

        //if you are localhost track which profile you are
        //being simulated to be on using a cookie and also send this
        //to the server so it also knows you have switched

        if (window.location.hostname === 'localhost') {
            simulatedProfile =
                getCookie('simulatedLocalRedirectProfile');
            if (simulatedProfile) {
                myInit.headers['x-profile'] = simulatedProfile;
            }
        }

        const isPublicApi = (apiName === PUBLIC_API_NAME);
        if (!isPublicApi && customAuthorizationEnabled) {
            try {
                let session = await Auth.currentSession()
                myInit.headers['Authorization'] = session.getIdToken().getJwtToken();
            } catch (e) {
                console.error("Unable to obtain a valid session and JWT token!!", e);
                throw Error("User is not authenticated")
            }
        }

        let response = await method(apiName, path, myInit);

        // So this is a workaround that will download the response from S3 instead of response from API.
        // This is done to work around limitation in Lambda that limits the response to 6MB.
        if (response && response.data && response.data.s3Response) {

            const result = await Storage.get(response.data.s3File, { contentType: 'text/plain', download: true });
            const responseStr = await result.Body.text();
            response.data = JSON.parse(responseStr)
        }

        return response.data;
    } catch (error) {
        if (processError) {
            const code = error && error.response && error.response.status;

            if (code === 401 || ((typeof error === 'string' || error instanceof String) && error.toLowerCase().includes("not authenticated"))) {
                await showAlert({
                    title: 'Logged out',
                    text: "You were logged out for security reasons. Please login again.",
                    icon: "error",
                    buttons: true
                });

                return await UserActions.signOut();
            }

            if (code === 426) {
                const profile = error?.response?.data?.profile
                const message = error?.response?.data?.message
                if (profile) {
                    //if you are localhost then set your "simulatedProfile"
                    //to remember that a redirect has indeed occurred
                    //otherwise do what the server tells you to and do a switch
                    console.info(`Upgrade detected moving to ${profile}`, message);
                    if (window.location.hostname === 'localhost') {
                        setCookie('simulatedLocalRedirectProfile', profile, '/',
                            30)
                        window.location = `/${profile}/index.html`
                    } else {
                        window.location = `/${profile}/index.html`
                    }

                    return;
                } else {
                    //what do you do if no profile was sent back?? logout? catastrophe?
                }
            }

            if (code === 503) {
                const subCode = error?.response?.data?.subCode;
                if (subCode === 'NO_PERM') {
                    throw {
                        title: 'Temporary Outage',
                        errorMessage: "We are currently performing some maintenance on our servers. Please try again in a few moments",
                    };
                }
            }
            //what to do in read only mode

            const data = error && error.response && error.response.data || error;
            const text = data && data.errorMessage || data && data.toString() || "Something went wrong";
            const title = "Something went wrong"

            throw {
                title,
                text,
                ...data
            }

        }

        throw error;
    }
}

function Utf8ArrayToStr(array) {
    var out, i, len, c;
    var char2, char3;

    out = "";
    len = array.length;
    i = 0;
    while (i < len) {
        c = array[i++];
        switch (c >> 4) {
            case 0: case 1: case 2: case 3: case 4: case 5: case 6: case 7:
                // 0xxxxxxx
                out += String.fromCharCode(c);
                break;
            case 12: case 13:
                // 110x xxxx   10xx xxxx
                char2 = array[i++];
                out += String.fromCharCode(((c & 0x1F) << 6) | (char2 & 0x3F));
                break;
            case 14:
                // 1110 xxxx  10xx xxxx  10xx xxxx
                char2 = array[i++];
                char3 = array[i++];
                out += String.fromCharCode(((c & 0x0F) << 12) |
                    ((char2 & 0x3F) << 6) |
                    ((char3 & 0x3F) << 0));
                break;
        }
    }

    return out;
}
